import React, { FC, useRef, useState, useEffect } from 'react';
import { UploadIcon } from 'cokitchen-icons';
import { getFileSize } from 'utils/functions';
import * as XLSX from 'xlsx';
import { ENUM_XLS_SHEET_TITLES } from 'utils/enum';

type XlsUploaderProps = {
  title?: string;
  setAddons: (arg: any) => void;
  file: File | null;
  setFile: (item: File | null) => void;
};
const BaseAddonCategoryUploader: FC<XlsUploaderProps> = ({
  title = 'Upload Xls File',
  setAddons,
  file,
  setFile
}) => {
  const [percent, setPercent] = useState(0);
  const [timeLeft] = useState('');
  const [uploading, setUploading] = useState(false);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [allowDrop, setAllowDrop] = useState(true);

  const handleFileDrop = (e: any) => {
    if (allowDrop) {
      e.preventDefault();
      const files = e.dataTransfer.files;
      if (files) setFile(files[0]);
    }
  };

  const handleDragOver = (e: any) => {
    if (allowDrop) e.preventDefault();
  };
  const fileUploadRef = useRef<HTMLInputElement | null>(null);

  const handleFileUpload = (e: any) => {
    setUploading(true);
    setHasUploaded(false);
    const reader = new FileReader();
    reader.readAsBinaryString(e.target.files[0]);
    setFile(e.target.files[0]);
    reader.onload = (e: any) => {
      const data = e.target.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const sheetName = ENUM_XLS_SHEET_TITLES.BASE_ADDON_CATEGORY;
      const baseAddonCategories = sheetName;
      const formatBaseAddonCategories = getCleanRows(
        XLSX.utils.sheet_to_json(workbook.Sheets[baseAddonCategories])
      );
      setAddons(formatBaseAddonCategories);
    };
    setUploading(false);
    setHasUploaded(true);
  };

  const getCleanRows = (rows: Array<Record<string, any>>) => {
    return rows.reduce((rows: Array<Record<string, any>>, row: Record<string, any>) => {
      const cleanRow: Record<string, any> = {};
      for (const key in row) {
        const cleanKey = key.toLowerCase().replace(/\s/g, '_');
        if (typeof row[key] === 'string') {
          if (cleanKey === 'ref') {
            cleanRow[cleanKey] = row[key].trim().replace(/\s/g, '');
          } else {
            cleanRow[cleanKey] = row[key].trim();
          }
        } else cleanRow[cleanKey] = row[key];
      }
      rows.push(cleanRow);
      return rows;
    }, []);
  };

  useEffect(() => {
    if (uploading) {
      setAllowDrop(false);
    } else {
      setPercent(0);
      setAllowDrop(true);
    }
  }, [uploading]);
  return (
    <>
      <div className='max-h-[330px] overflow-auto pt-4 pb-2 px-6 rounded-[6px] border border-[#EDEDEE]  space-y-5'>
        <h4 className='font-medium'>{title}</h4>
        <div
          role='button'
          className={`border border-dashed border-[#D9D9D9] h-[100px] flex items-center justify-center gap-1 w-full rounded-md text-xs ${
            uploading && 'cursor-not-allowed '
          }`}
          onDrop={handleFileDrop}
          onDragOver={handleDragOver}
          onClick={() => {
            if (!uploading) fileUploadRef?.current?.click();
          }}>
          <UploadIcon className='mr-3' />
          <p className='text-gray-4'> Drop {!file ? 'your' : 'another'} file here or </p>
          <span className={`${uploading ? 'text-gray-4 opacity-40' : 'font-medium text-red-5'}`}>
            browse
          </span>
        </div>
        {file ? (
          <div className='bg-[#F5F4F4] rounded px-8 py-3 space-y-5'>
            <div className='flex items-center gap-6 flex-wrap'>
              <div className='w-[50px] h-[50px] rounded-full bg-white p-2'>
                <div
                  className='h-[100%] w-[100%] rounded-[100%]'
                  style={{
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                />
              </div>

              <div className='grow space-y-2'>
                <label className='text-sm gap-2 font-medium text-[#4B4D4E]'>{file.name}</label>
                <div className='text-xs flex text-gray-5 items-center gap-[7px]'>
                  <p>{getFileSize(file.size)}</p>
                  <div className='w-1 h-1 bg-gray-5 rounded-full' />
                  {uploading && <p>{timeLeft} left</p>}
                </div>
              </div>
              {hasUploaded && (
                <span className='self-end text-xs text-gray-5'>Upload Complete!</span>
              )}
              {uploading && <span className='self-end text-xs text-gray-5'>{percent}%</span>}
            </div>
            {uploading && (
              <div className='relative overflow-hidden h-[6px] rounded-md w-full bg-white'>
                <div
                  style={{
                    height: '100%',
                    width: `${percent}%`,
                    transition: 'width .8s'
                  }}
                  className={`bg-red-5 rounded-full`}
                />
              </div>
            )}
          </div>
        ) : null}
      </div>
      <input
        ref={fileUploadRef}
        type='file'
        accept='.xlsx, .xls'
        hidden
        onChange={handleFileUpload}
      />
    </>
  );
};

export default BaseAddonCategoryUploader;
